import { useState, useCallback, useMemo, useEffect } from "react";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  CustomError,
  LEVEL_TYPE,
} from "api/interfaces/commonInterface.interface";
import { TableColumnsType } from "antd";
import { CommonTable } from "components/atoms/table/AntdTable";
import {
  SUBSCRIPTION_TYPE,
  SubscriptionKeyListReqeust,
  SubscriptionKeyListType,
  SubscriptionKeyListResponse,
  SelectedRowAccountInfo,
  ChangeWidthColumn,
} from "api/interfaces/subscription.interface";
import SearchInput from "components/atoms/input/search/SearchInput";
import {
  initialQueryKeyInfo,
  useKeyTable,
} from "hook/subscription/useKeyTable";
import { getActivateStatus } from "utils/SubscriptionUtil";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { SubscriptionKeyListStyled } from "pages/subscription/SubscriptionStyled";
import { isNull } from "lodash";
import { SORT_DIRECTION } from "api/interfaces/commonInterface.interface";
import { useLocation } from "react-router-dom";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { useQueryClient } from "react-query";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import { postCovaFreeKeyRenew } from "api/subscriptionAPI";
import TinyButton from "components/atoms/buttons/TinyButton";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { FaArrowsRotate } from "react-icons/fa6";
import { useFlag } from "@unleash/proxy-client-react";

type Props = {
  type: SUBSCRIPTION_TYPE | "REQUEST";
  selectedRowAccount: SelectedRowAccountInfo;
};

export default function HistoryKeyList(props: Props): JSX.Element {
  const initalQueryInfo = {
    ...initialQueryKeyInfo,
    pageLimit: 10,
    type:
      props.type === SUBSCRIPTION_TYPE.STANDARD
        ? SUBSCRIPTION_TYPE.STANDARD
        : SUBSCRIPTION_TYPE.STORAGE,
  };
  const location = useLocation();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const isEU = selectedAccount.accountLevel === LEVEL_TYPE.EU;
  const isStorage = props.type === SUBSCRIPTION_TYPE.STORAGE;
  const isStandard = props.type === SUBSCRIPTION_TYPE.STANDARD;
  const isCovaFreeKeyRenew = useFlag("feature_subscription_cova_renew");
  const [queryInfo, setQueryInfo] = useState<SubscriptionKeyListReqeust>({
    ...initalQueryInfo,
  });

  const {
    subscriptionKeyList,
    useQueryKeyList,
    commonKeyColumns,
    onChangePage,
    changeWidthColumn,
  } = useKeyTable({
    queryInfo,
    setQueryInfo,
    selectedRowAccountId: props.selectedRowAccount.accountId,
  });

  const { handleErrorNotify, lang, notifySuccess } = useApiError();
  const queryClient = useQueryClient();

  const mutationRenew = useStatusMutation(postCovaFreeKeyRenew, {
    onSuccess: () => {
      notifySuccess(lang.label.subscription.cova.renew.success);
      queryClient.invalidateQueries("getSubscriptionKeyList");
      queryClient.invalidateQueries("getSubscriptionDetailSummary");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.cova.renew.fail);
    },
  });

  const handleRenew = useCallback(
    (row: SubscriptionKeyListType) => {
      if (!row.dwKey || !mutationRenew.idle) return;
      mutationRenew.mutate({
        accountId: props.selectedRowAccount.accountId,
        dwKey: row.dwKey,
      });
    },
    [mutationRenew, props.selectedRowAccount.accountId]
  );

  const data: SubscriptionKeyListType[] = useMemo(() => {
    let tempData: SubscriptionKeyListType[] = subscriptionKeyList.map(
      (res: SubscriptionKeyListResponse) => {
        let purchased = res.channel;
        if (isStorage) {
          purchased = res.capacity;
        }
        return {
          ...res,
          key: res.dwKey,
          purchased,
          status: getActivateStatus(res),
        };
      }
    );

    return tempData;
  }, [subscriptionKeyList]);

  const fixWidthCell: ChangeWidthColumn[] = [
    {
      dataIndex: "type",
      hidden: true,
    },
    {
      dataIndex: "dwKey",
      width: undefined,
    },
    {
      dataIndex: "price",
      hidden: isEU,
    },
    {
      dataIndex: "purchased",
      title: isStandard ? "Channels" : "Capacity (TB)",
      width: "15%",
    },
    {
      dataIndex: "duration",
      width: "20%",
    },
    {
      dataIndex: "createdAt",
      title: "Created",
      render: (value, row) => getAccountTimeFormatDate(value, selectedAccount),
      width: "15%",
    },
    {
      dataIndex: "activateDate",
      width: 120,
    },
  ];

  const columns: TableColumnsType<SubscriptionKeyListType> = [
    ...changeWidthColumn(commonKeyColumns, fixWidthCell),
    {
      title: "",
      key: "renew",
      width: 90,
      render: (_value, row) =>
        row.dwKey.startsWith("3") && isCovaFreeKeyRenew ? (
          <RBACWrapper requiredPermissions="manageSubscription">
            <TinyButton
              label="Renew"
              icon={<FaArrowsRotate />}
              onClickBtn={() => handleRenew(row)}
              disabled={!row.dwKey || !mutationRenew.idle}
            />
          </RBACWrapper>
        ) : null,
      hidden: !isCovaFreeKeyRenew,
    },
  ];

  const onClickSearch = useCallback((string: string) => {
    setQueryInfo((info) => {
      return {
        ...info,
        keyword: string,
        pageNum: 0,
      };
    });
  }, []);

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setQueryInfo((info: SubscriptionKeyListReqeust) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? initalQueryInfo.pageLimit
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? initalQueryInfo.sortType
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? initalQueryInfo.sortDirection
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
          type: isNull(changeParam.get("type"))
            ? initalQueryInfo.type
            : (changeParam.get("type") as SUBSCRIPTION_TYPE),
        };
      });
    } else {
      setQueryInfo({ ...initalQueryInfo });
    }
  }, [location]);

  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: useQueryKeyList.refetch,
  });

  return (
    <SubscriptionKeyListStyled.Container>
      <SubscriptionKeyListStyled.SearchWrapper>
        <div>
          <SearchInput
            keyword={queryInfo.keyword}
            placeholder="Search"
            onClickSearch={onClickSearch}
          />
        </div>
      </SubscriptionKeyListStyled.SearchWrapper>
      <CommonTable
        columns={columns}
        dataSource={data}
        queryInfo={queryInfo}
        onChangePage={(page: number) => onChangePage(page, 0)}
        loading={useQueryKeyList.isFetching}
      />
    </SubscriptionKeyListStyled.Container>
  );
}
